/* Screen 2 - Enhance Coverage */

import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getBniTravelCoverPrices,
  getTravelCoverPrices,
} from "../../../../api/quoteServices";
import closeicon from "../../../../assets/images/close.svg";
import erroricon from "../../../../assets/images/error.svg";
import BackdropModal from "../../../../components/common/modal/backdropModal";
import TitleDescription from "../../../../components/newMotorPolicy/titleDescription/titleDescription";
import ViewSummaryPopup from "../../../../components/newMotorPolicy/veiwSummaryPopup/viewSummaryPopup";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import useTravelRenewalFlow, {
  TRAVEL_RENEWAL_SELECTED_COVERS,
  TRAVEL_RENEWAL_SELECTED_PLAN,
} from "../../../../hooks/useTravelRenewalFlow";
import { PolicyViewResponse } from "../../../../redux/features/motor/getQuoteSlice";
import {
  selectNewMotorForm,
  setMotorFormData,
} from "../../../../redux/features/newMotorFormSlice";
import { currencyFormatter } from "../../../../utils";
import TravelRenewalLayout from "../components/TravelRenewalLayout";

const EnhanceCoveragePage = ({ handleNavigationToRenewalHub }) => {
  const dispatch = useDispatch();
  const translate = useLanguageTranslate();

  // Get policy details from Redux
  const policyDetails = useSelector(PolicyViewResponse);
  const currentCovers = policyDetails?.PolicyHeader?.Covers || [];

  // Get selected plan from Redux (if user changed plan)
  const selectedPlan = useSelector(selectNewMotorForm)?.[TRAVEL_RENEWAL_SELECTED_PLAN];

  // Restore cover selection from Redux if available (fixes back navigation losing selection)
  const savedCoversFromRedux = useSelector(selectNewMotorForm)?.[TRAVEL_RENEWAL_SELECTED_COVERS];

  // Flow hook
  const { goToStep, planChanged } = useTravelRenewalFlow();

  // State for cover options and loading
  const [loadingCoverPrices, setLoadingCoverPrices] = useState(true);
  const [formState, setFormState] = useState({ coverOptionsState: [] });
  const [tempPrice, setTempPrice] = useState(0);

  // Modal state
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [selectedCoverInfo, setSelectedCoverInfo] = useState(null);
  const [showSummaryModal, setShowSummaryModal] = useState(false);

  // Get the plan code to use (selected plan if changed, otherwise current plan)
  const planCodeToUse = selectedPlan?.code || policyDetails?.PolicyHeader?.PlanCode;

  // Fetch cover prices from travel API
  const fetchCoverPrices = async () => {
    if (!policyDetails?.PolicyHeader) {
      return;
    }

    setLoadingCoverPrices(true);

    try {
      const policyHeader = policyDetails.PolicyHeader;

      // Convert policy dates to required format
      const rawDepartureDate = policyHeader.FromDateStr || policyHeader.FromDate;
      const rawReturnDate = policyHeader.ToDateStr || policyHeader.ToDate;

      const departureDate = formatDateForAPI(rawDepartureDate);
      const returnDate = formatDateForAPI(rawReturnDate);

      if (!departureDate || !returnDate) {
        setLoadingCoverPrices(false);
        return;
      }

      // New travel plan codes use the new cover-prices API, old codes use legacy API
      const newPlanCodes = ["SCHENGENB", "WLEXB", "WLB"];
      const isNewPlanCode = newPlanCodes.includes(planCodeToUse);

      // Map plan code to API format
      const planCodeMapping = {
        PL: "platinum",
        GO: "gold",
        SILVER: "silver",
      };

      const selectedPlanForAPI = isNewPlanCode
        ? planCodeToUse.toLowerCase() // getBniTravelCoverPrices accepts 'schengenb' / 'wlexb' / 'wlb'
        : planCodeMapping[planCodeToUse] || "silver";
      const planType = policyHeader.PlanType || "individual";

      const coverPricesFormData = {
        selectedPlan: selectedPlanForAPI,
        departureDate,
        returnDate,
        planType,
      };

      const result = isNewPlanCode
        ? await getBniTravelCoverPrices(coverPricesFormData)
        : await getTravelCoverPrices(coverPricesFormData);

      if (result.success && result.covers) {
        // Transform API covers to component format
        // Always use fresh prices from API, but preserve the user's toggle selection if they've been here before
        const savedCovers = savedCoversFromRedux?.coverOptionsState || [];
        const transformedCovers = result.covers.map((cover) => {
          const isDefaultIncluded = isAlreadyIncluded(cover.CoverCode);
          // Preserve user's selection state if it exists in Redux (for back navigation)
          const savedCover = savedCovers.find((s) => s.coverCode === cover.CoverCode);
          return {
            id: cover.CoverCode,
            name: getCoverDisplayName(cover.CoverCode, cover.CoverName),
            name_ar: getCoverDisplayName(cover.CoverCode, cover.CoverName, true),
            description: getDescriptionForCover(cover.CoverCode),
            description_ar: getDescriptionForCover(cover.CoverCode, true),
            price: parseFloat(cover.Price || 0), // Always fresh price from API
            coverCode: cover.CoverCode,
            included: savedCover ? savedCover.included : isDefaultIncluded,
            showCover: true,
            defaultIncludedInPlan: isDefaultIncluded,
            includedWithOther: false,
            disabled: false,
          };
        });

                const initialFormState = { coverOptionsState: transformedCovers };
        setFormState(initialFormState);

        // Store initial state in Redux
        dispatch(
          setMotorFormData({
            form_name: TRAVEL_RENEWAL_SELECTED_COVERS,
            data: initialFormState,
          })
        );
      } else {
                setFormState({ coverOptionsState: [] });
      }
    } catch (error) {
      console.error("Error fetching travel cover prices:", error);
            setFormState({ coverOptionsState: [] });
    } finally {
      setLoadingCoverPrices(false);
    }
  };

  // Helper function to get correct display names for cover codes
  const getCoverDisplayName = (coverCode, apiName = "", isArabic = false) => {
    const displayNames = {
      CPER137: {
        en: "Summer Activities",
        ar: "الأنشطة الصيفية",
      },
      CPER17: {
        en: "Winter Activities",
        ar: "الأنشطة الشتوية",
      },
    };

    return displayNames[coverCode]
      ? displayNames[coverCode][isArabic ? "ar" : "en"]
      : apiName;
  };

  // Helper function to get description for cover codes
  const getDescriptionForCover = (coverCode, isArabic = false) => {
    const descriptions = {
      CPER137: {
        en: "Coverage for summer sports and activities during your trip",
        ar: "تغطية الأنشطة والرياضات الصيفية أثناء رحلتك",
      },
      CPER17: {
        en: "Coverage for winter sports and activities during your trip",
        ar: "تغطية الأنشطة والرياضات الشتوية أثناء رحلتك",
      },
    };

    return descriptions[coverCode]
      ? descriptions[coverCode][isArabic ? "ar" : "en"]
      : "";
  };

  // Helper function to check if cover is already included in policy
  const isAlreadyIncluded = (coverCode) => {
    return currentCovers.some((cover) => cover.CoverCode === coverCode);
  };

  // Helper function to format dates for API
  const formatDateForAPI = (dateValue) => {
    if (!dateValue) return null;

    try {
      let date;

      if (
        typeof dateValue === "number" ||
        (typeof dateValue === "string" && /^\d+$/.test(dateValue))
      ) {
        const timestamp = parseInt(dateValue);
        if (timestamp > 1000000000) {
          date = new Date(timestamp * 1000);
        } else {
          date = new Date(timestamp);
        }
      } else if (typeof dateValue === "string" && dateValue.includes("/")) {
        const parts = dateValue.split("/");
        if (parts.length === 3) {
          const day = parseInt(parts[0]);
          const month = parseInt(parts[1]) - 1;
          const year = parseInt(parts[2]);
          date = new Date(year, month, day);
        }
      } else {
        date = new Date(dateValue);
      }

      if (isNaN(date.getTime())) {
        return null;
      }

      return date.toISOString().split("T")[0];
    } catch (error) {
      return null;
    }
  };

  // Always fetch fresh cover prices from API when policy data or selected plan changes
  useEffect(() => {
    if (policyDetails?.PolicyHeader) {
      fetchCoverPrices();
    }
  }, [policyDetails, planCodeToUse]);

  // Get base premium only from plan-prices API (when plan changed); otherwise 0 — no premium API yet
  const getBasePremium = () => {
    if (planChanged && selectedPlan?.totalPremium) {
      return parseFloat(selectedPlan.totalPremium) || 0;
    }
    return 0;
  };

  // Calculate total premium: base premium + additional covers
  useEffect(() => {
    const basePremium = getBasePremium();

    if (formState?.coverOptionsState?.length > 0) {
      const additionalPremium = formState.coverOptionsState
        .filter((opt) => opt.included && !opt.defaultIncludedInPlan)
        .reduce((sum, cover) => sum + (parseFloat(cover.price) || 0), 0);
      const additionalTax = additionalPremium * 0.1;
      const totalPremium = basePremium + additionalPremium + additionalTax;
      setTempPrice(totalPremium);
    } else {
      setTempPrice(basePremium);
    }
  }, [formState, policyDetails, planChanged, selectedPlan]);

  // Updates selected cover
  const updateOptionValues = (coverCode, updates, price) => {
    const updatedCoverOptions = formState.coverOptionsState.map((option) => {
      if (option.coverCode === coverCode) {
        return {
          ...option,
          ...updates,
          price: updates.price || option.price || price || 0,
        };
      }
      return option;
    });

    const update = {
      ...formState,
      coverOptionsState: updatedCoverOptions,
    };

    setFormState(update);

    // Store in Redux for the review page
    dispatch(
      setMotorFormData({
        form_name: TRAVEL_RENEWAL_SELECTED_COVERS,
        data: update,
      })
    );
  };

  // Handle showing cover info modal
  const handleShowCoverInfo = (coverCode) => {
    setSelectedCoverInfo(coverCode);
    setShowInfoModal(true);
  };

  // Handle back button - go to Select Plan if plan was changed, otherwise Review Policy
  const handleBackClick = () => {
    if (planChanged) {
      goToStep(1); // Go to Select Plan
    } else {
      goToStep(0); // Go to Review Policy Plan
    }
  };

  // Next page handler
  const handleContinue = () => {
    goToStep(3); // Go to Review & Confirm
  };

  // Handle show summary modal
  const handleShowSummary = () => {
    setShowSummaryModal(true);
  };

  return (
    <>
      <TravelRenewalLayout
        is_hide_back_btn={false}
        show_close_btn={false}
        isFirstStep={false}
        backClickHandler={handleBackClick}
        btn_onClick={handleContinue}
        btn_disabled={loadingCoverPrices}
        show_summary={true}
        price={tempPrice}
        onShowSummary={handleShowSummary}
        btn_label={translate("Continue", "التالي")}
      >
        <div className="flex flex-col items-center px-6 max767:p-0 mx-auto pt-0 py-5 w-full pt-0">
          <div className="w-[32%] max767:w-full mx-auto ipad:min-w-[66%] pb-5 max767:px-4 pt-4">
            <div className="text-center max767:text-left rtl:max767:text-right mb-8">
              <TitleDescription
                title={translate("Enhance Your Coverage", "عزّز تغطيتك")}
                description={translate(
                  "Would you like to add extra covers and benefits to your policy?",
                  "هل ترغب في إضافة تغطيات ومزايا إضافية إلى وثيقتك؟"
                )}
              />
            </div>

            {Array.isArray(formState.coverOptionsState) &&
            formState.coverOptionsState.length > 0 ? (
              <div className="space-y-4 mb-8 max-w-4xl mx-auto">
                {formState.coverOptionsState
                  .slice()
                  .reverse()
                  .map((option) => (
                    <div
                      key={option.coverCode}
                      className={`bg-white rounded-lg border-2 p-4 transition-all duration-300 ${
                        option.included
                          ? "border-[#00719A]"
                          : "border-[#DDE2E4]"
                      } ${
                        option.defaultIncludedInPlan === true
                          ? "opacity-70 cursor-not-allowed"
                          : ""
                      }`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center">
                          <span className="text-base font-normal rtl:font-medium text-[#252C32] rtl:text-[#48535B] mr-2 rtl:mr-0 rtl:ml-2">
                            {translate(option.name, option.name_ar)}
                          </span>
                          {option.description && (
                            <button
                              onClick={() => handleShowCoverInfo(option.coverCode)}
                              className="w-4 h-4 flex items-center justify-center hover:opacity-70 transition-opacity"
                            >
                              <img
                                src={erroricon}
                                alt="info"
                                className="w-4 h-4 mx-1"
                              />
                            </button>
                          )}
                        </div>

                        {option.defaultIncludedInPlan ? (
                          <span className="bg-[#EEF0F2] text-[#252C32] px-3 py-1 rounded-md text-sm font-medium">
                            {translate("Included in the plan", "شامل ضمن الخطة")}
                          </span>
                        ) : option.includedWithOther ? (
                          <span className="bg-[#EEF0F2] text-[#252C32] px-3 py-1 rounded-md text-sm font-medium">
                            {translate("Included", "متضمن")}
                          </span>
                        ) : (
                          <label
                            className={`relative inline-flex items-center ${
                              loadingCoverPrices
                                ? "cursor-not-allowed opacity-50"
                                : "cursor-pointer"
                            }`}
                          >
                            <input
                              type="checkbox"
                              className="sr-only"
                              checked={option.included}
                              disabled={option.disabled || loadingCoverPrices}
                              onChange={() =>
                                !loadingCoverPrices &&
                                updateOptionValues(
                                  option.coverCode,
                                  { included: !option.included },
                                  option.price
                                )
                              }
                            />
                            <div
                              className={`w-8 h-5 rounded-full transition-colors ${
                                option.included ? "bg-[#00A9E7]" : "bg-gray-300"
                              }`}
                            >
                              <div
                                className={`size-4 bg-white rounded-full shadow transform transition-transform mt-0.5 ${
                                  option.included
                                    ? "translate-x-3.5 rtl:-translate-x-3.5"
                                    : "translate-x-[0.15rem] rtl:-translate-x-[0.15rem]"
                                }`}
                              ></div>
                            </div>
                          </label>
                        )}
                      </div>

                      {/* Show price badge when inactive or active */}
                      {!option.defaultIncludedInPlan && !option.includedWithOther && (
                        <div className="flex justify-end">
                          {loadingCoverPrices ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#00719A]"></div>
                          ) : (
                            <span
                              className={`px-2 py-0.5 rounded-full text-xs font-medium uppercase ${
                                option.included
                                  ? "bg-[#00719A] text-white"
                                  : "bg-[#EEF0F2] text-[#252C32]"
                              }`}
                            >
                              {translate(
                                `+ BHD ${currencyFormatter(option.price)}`,
                                `+ ${currencyFormatter(option.price)} د.ب`
                              )}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            ) : (
              <div className="text-center py-8">
                {loadingCoverPrices ? (
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00719A] mx-auto"></div>
                ) : (
                  <p className="text-lg text-[#48535B]">
                    {translate(
                      "No additional covers available for this plan.",
                      "لا توجد تغطيات إضافية متاحة لهذه الخطة."
                    )}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </TravelRenewalLayout>

      {/* Info Modal */}
      <BackdropModal
        show={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        hideClosebtn
      >
        <div className="flex justify-between items-center mb-4 -mt-6">
          <h2 className="text-[#1A2024] max767:text-black text-2xl max-767:text-XL not-italic font-semibold leading-6">
            {selectedCoverInfo === "CPER17"
              ? translate(
                  "Winter Activities Coverage",
                  "تغطية الأنشطة الشتوية"
                )
              : selectedCoverInfo === "CPER137"
              ? translate("Summer Activities Coverage", "تغطية الأنشطة الصيفية")
              : translate("Coverage Information", "معلومات التغطية")}
          </h2>
          <button
            onClick={() => setShowInfoModal(false)}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <img src={closeicon} alt="Close" className="w-6 h-6" />
          </button>
        </div>
        <div className="w-full max-h-[80vh] max-w-[540px]">
          {selectedCoverInfo === "CPER17" ? (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-[#000] mb-3">
                  {translate("Snow Sports:", "الرياضات الثلجية:")}
                </h3>
                <ul className="space-y-2 text-[#000] pl-2.5">
                  <li>
                    •{" "}
                    {translate(
                      "All skiing variations (traditional, land, mono, cross-country)",
                      "جميع أنواع التزلج (التقليدي والأرضي والأحادي والتزلج البري على الثلج)"
                    )}
                  </li>
                  <li>
                    •{" "}
                    {translate(
                      "Advanced skiing (heli-skiing, guided off-piste)",
                      "التزلج المتقدم (التزلج على الجليد و التزلج خارج الزحلقة)"
                    )}
                  </li>
                  <li>
                    •{" "}
                    {translate(
                      "Snowboarding & ski boarding",
                      "التزلج على الجليد والتزلج على الألواح"
                    )}
                  </li>
                  <li>
                    •{" "}
                    {translate(
                      "Snow recreation (ice skating, sledding, tobogganing)",
                      "الاستجمام على الجليد (التزلج على الجليد و التزحلق على الجليد)"
                    )}
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-medium text-[#000] mb-3">
                  {translate("Other Winter Activities:", "أنشطة شتوية أخرى:")}
                </h3>
                <ul className="space-y-2 text-[#000] pl-2.5">
                  <li>
                    •{" "}
                    {translate(
                      "Winter hiking & trekking",
                      "المشي لمسافات طويلة والتنزه الشتوي"
                    )}
                  </li>
                  <li>
                    •{" "}
                    {translate(
                      "Ice climbing",
                      "تسلق الجليد"
                    )}
                  </li>
                  <li>
                    •{" "}
                    {translate(
                      "Dog sledding & snowshoeing",
                      "التزلج بالكلاب والمشي بالمضارب الثلجية"
                    )}
                  </li>
                  <li>
                    • {translate("Ice fishing", "الصيد على الجليد")}
                  </li>
                </ul>
              </div>

              <div>
                <p className="text-sm text-[#000]">
                  <span className="font-bold">
                    {translate("Important:", "هام:")}
                  </span>{" "}
                  {translate(
                    "Professional competitions and tournaments are not covered.",
                    "لا يتم تغطية المسابقات والبطولات الاحترافية."
                  )}
                </p>
                <p className="text-xs text-[#5B6871] mt-2">
                  {translate(
                    "This protection ensures medical care and emergency assistance if accidents occur during these recreational activities.",
                    "تضمن هذه الحماية الرعاية الطبية والمساعدة الطارئة في حالة وقوع حوادث أثناء هذه الأنشطة الترفيهية."
                  )}
                </p>
              </div>
            </div>
          ) : selectedCoverInfo === "CPER137" ? (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-[#000] mb-3">
                  {translate("Water Sports:", "رياضات مائية:")}
                </h3>
                <ul className="space-y-2 text-[#000] pl-2.5">
                  <li>
                    •{" "}
                    {translate("Surfing", "ركوب الأمواج")}
                  </li>
                  <li>
                    •{" "}
                    {translate("Windsurfing", "ركوب الأمواج الشراعي")}
                  </li>
                  <li>
                    •{" "}
                    {translate("Sailing", "الإبحار")}
                  </li>
                  <li>
                    •{" "}
                    {translate("Water skiing", "التزلج المائي")}
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-medium text-[#000] mb-3">
                  {translate("Outdoor Summer Activities:", "أنشطة صيفية خارجية:")}
                </h3>
                <ul className="space-y-2 text-[#000] pl-2.5">
                  <li>
                    •{" "}
                    {translate("Beach sports", "رياضات الشاطئ")}
                  </li>
                  <li>
                    •{" "}
                    {translate("Snorkelling", "الغطس الحر")}
                  </li>
                  <li>
                    •{" "}
                    {translate("Recreational fishing", "صيد الأسماك الترفيهي")}
                  </li>
                </ul>
              </div>

              <div>
                <p className="text-sm text-[#000]">
                  <span className="font-bold">
                    {translate("Important:", "هام:")}
                  </span>{" "}
                  {translate(
                    "Professional competitions and tournaments are not covered.",
                    "لا يتم تغطية المسابقات والبطولات الاحترافية."
                  )}
                </p>
                <p className="text-xs text-[#5B6871] mt-2">
                  {translate(
                    "This protection ensures medical care and emergency assistance if accidents occur during these recreational activities.",
                    "تضمن هذه الحماية الرعاية الطبية والمساعدة الطارئة في حالة وقوع حوادث أثناء هذه الأنشطة الترفيهية."
                  )}
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-[#000]">
                {translate(
                  "Coverage information not available.",
                  "معلومات التغطية غير متاحة."
                )}
              </p>
            </div>
          )}
        </div>
      </BackdropModal>

      {/* Summary Modal */}
      <ViewSummaryPopup
        show={showSummaryModal}
        setShow={setShowSummaryModal}
        passedFrom="TravelRenewal"
      />
    </>
  );
};

export default EnhanceCoveragePage;
